import styled from "@emotion/styled";
import {
  Box,
  Breadcrumbs,
  Card,
  CardContent,
  Chip,
  CircularProgress,
  Grid,
  Link,
  Stack,
  Typography,
} from "@mui/material";
import { PageWithTitle } from "src/Common";
import NavigateNextIcon from "@mui/icons-material/NavigateNext";
import { Link as RouterLink } from "react-router-dom";
import {
  AGGREGATOR_HEALTH,
  PROFITS_WS_HEALTH,
  CRYPTO_STATE_HEALTH,
  CALCULATOR_HEALTH,
} from "src/Api/endpoints";
import { useQuery } from "react-query";
import {
  getAggregatorHealth,
  getProfitsWsHealth,
  getCryptoStateHealth,
  getCalculatorHealth,
} from "src/Api";

const REFETCH_INTERVAL = 15000;

function formatUptime(seconds: number): string {
  const days = Math.floor(seconds / 86400);
  const hours = Math.floor((seconds % 86400) / 3600);
  const minutes = Math.floor((seconds % 3600) / 60);
  if (days > 0) return `${days}d ${hours}h ${minutes}m`;
  if (hours > 0) return `${hours}h ${minutes}m`;
  return `${minutes}m`;
}

function formatBytes(bytes: number): string {
  const mb = bytes / (1024 * 1024);
  return `${mb.toFixed(0)} MB`;
}

function getStatusColor(
  status?: string,
  isError?: boolean
): "success" | "error" | "warning" | "default" {
  if (isError) return "error";
  if (!status) return "default";
  const s = status.toLowerCase();
  if (s === "ok" || s === "healthy" || s === "running") return "success";
  if (s === "degraded") return "warning";
  return "error";
}

function getStatusLabel(status?: string, isError?: boolean): string {
  if (isError) return "Unreachable";
  if (!status) return "Unknown";
  const s = status.toLowerCase();
  if (s === "ok" || s === "healthy" || s === "running") return "Healthy";
  if (s === "degraded") return "Degraded";
  return "Unhealthy";
}

export const ServicesStatus = () => {
  const {
    data: aggregator,
    isLoading: aggLoading,
    isError: aggError,
  } = useQuery(AGGREGATOR_HEALTH, getAggregatorHealth, {
    refetchInterval: REFETCH_INTERVAL,
    retry: false,
  });

  const {
    data: profitsWs,
    isLoading: pwsLoading,
    isError: pwsError,
  } = useQuery(PROFITS_WS_HEALTH, getProfitsWsHealth, {
    refetchInterval: REFETCH_INTERVAL,
    retry: false,
  });

  const {
    data: cryptoState,
    isLoading: csLoading,
    isError: csError,
  } = useQuery(CRYPTO_STATE_HEALTH, getCryptoStateHealth, {
    refetchInterval: REFETCH_INTERVAL,
    retry: false,
  });

  const {
    data: calculator,
    isLoading: calcLoading,
    isError: calcError,
  } = useQuery(CALCULATOR_HEALTH, getCalculatorHealth, {
    refetchInterval: REFETCH_INTERVAL,
    retry: false,
  });

  return (
    <PageWithTitle
      title={
        <Stack spacing={2}>
          <Breadcrumbs
            separator={<NavigateNextIcon fontSize="small" />}
            aria-label="breadcrumb"
          >
            <Link
              component={RouterLink}
              underline="hover"
              key="1"
              color="inherit"
              to="/arbitrage"
            >
              Arbitrage
            </Link>
            <Typography key="2" color="text.primary">
              Services Status
            </Typography>
          </Breadcrumbs>
        </Stack>
      }
      style={{ padding: "2rem 5%" }}
    >
      <Grid container spacing={3}>
        {/* Orderbook Aggregator */}
        <Grid item xs={12} sm={6}>
          <ServiceCard>
            <CardContent>
              <ServiceHeader>
                <ServiceName>Orderbook Aggregator</ServiceName>
                {aggLoading ? (
                  <CircularProgress size={20} />
                ) : (
                  <Chip
                    label={getStatusLabel(aggregator?.status, aggError)}
                    color={getStatusColor(aggregator?.status, aggError)}
                    size="small"
                  />
                )}
              </ServiceHeader>
              {!aggLoading && !aggError && aggregator && (
                <MetricsGrid>
                  <MetricItem>
                    <MetricLabel>Uptime</MetricLabel>
                    <MetricValue>
                      {formatUptime(aggregator.uptime ?? 0)}
                    </MetricValue>
                  </MetricItem>
                  <MetricItem>
                    <MetricLabel>Memory</MetricLabel>
                    <MetricValue>
                      {formatBytes(aggregator.memory?.rss ?? 0)}
                    </MetricValue>
                  </MetricItem>
                </MetricsGrid>
              )}
              {aggError && (
                <ErrorText>Service is not responding</ErrorText>
              )}
              <LastChecked>
                {aggregator?.timestamp
                  ? `Last checked: ${new Date(aggregator.timestamp).toLocaleTimeString()}`
                  : ""}
              </LastChecked>
            </CardContent>
          </ServiceCard>
        </Grid>

        {/* Arbitrage Calculator */}
        <Grid item xs={12} sm={6}>
          <ServiceCard>
            <CardContent>
              <ServiceHeader>
                <ServiceName>Arbitrage Calculator</ServiceName>
                {calcLoading ? (
                  <CircularProgress size={20} />
                ) : (
                  <Chip
                    label={getStatusLabel(calculator?.status, calcError)}
                    color={getStatusColor(calculator?.status, calcError)}
                    size="small"
                  />
                )}
              </ServiceHeader>
              {!calcLoading && !calcError && calculator && (
                <MetricsGrid>
                  <MetricItem>
                    <MetricLabel>Instance</MetricLabel>
                    <MetricValue>
                      {calculator.instance_id ?? "-"}
                    </MetricValue>
                  </MetricItem>
                  <MetricItem>
                    <MetricLabel>Pairs</MetricLabel>
                    <MetricValue>
                      {(calculator.cache_stats?.total_pairs ?? 0).toLocaleString()}
                    </MetricValue>
                  </MetricItem>
                  <MetricItem>
                    <MetricLabel>Active Opps</MetricLabel>
                    <MetricValue>
                      {(calculator.publishing_stats?.total_published ?? 0).toLocaleString()}
                    </MetricValue>
                  </MetricItem>
                </MetricsGrid>
              )}
              {calcError && (
                <ErrorText>Service is not responding</ErrorText>
              )}
              <LastChecked>
                {calculator?.uptime != null
                  ? `Uptime: ${formatUptime(calculator.uptime)}`
                  : ""}
              </LastChecked>
            </CardContent>
          </ServiceCard>
        </Grid>

        {/* Profits WebSocket */}
        <Grid item xs={12} sm={6}>
          <ServiceCard>
            <CardContent>
              <ServiceHeader>
                <ServiceName>Profits WebSocket</ServiceName>
                {pwsLoading ? (
                  <CircularProgress size={20} />
                ) : (
                  <Chip
                    label={getStatusLabel(profitsWs?.status, pwsError)}
                    color={getStatusColor(profitsWs?.status, pwsError)}
                    size="small"
                  />
                )}
              </ServiceHeader>
              {!pwsLoading && !pwsError && profitsWs && (
                <MetricsGrid>
                  <MetricItem>
                    <MetricLabel>Connections</MetricLabel>
                    <MetricValue>
                      {profitsWs.details?.activeConnections ?? 0}
                    </MetricValue>
                  </MetricItem>
                  <MetricItem>
                    <MetricLabel>Received</MetricLabel>
                    <MetricValue>
                      {(profitsWs.details?.stats?.totalReceived ?? 0).toLocaleString()}
                    </MetricValue>
                  </MetricItem>
                  <MetricItem>
                    <MetricLabel>Broadcasted</MetricLabel>
                    <MetricValue>
                      {(profitsWs.details?.stats?.totalBroadcasted ?? 0).toLocaleString()}
                    </MetricValue>
                  </MetricItem>
                </MetricsGrid>
              )}
              {pwsError && (
                <ErrorText>Service is not responding</ErrorText>
              )}
              <LastChecked>
                {profitsWs?.timestamp
                  ? `Last checked: ${new Date(profitsWs.timestamp).toLocaleTimeString()}`
                  : ""}
              </LastChecked>
            </CardContent>
          </ServiceCard>
        </Grid>

        {/* Crypto State Manager */}
        <Grid item xs={12} sm={6}>
          <ServiceCard>
            <CardContent>
              <ServiceHeader>
                <ServiceName>Crypto State Manager</ServiceName>
                {csLoading ? (
                  <CircularProgress size={20} />
                ) : (
                  <Chip
                    label={getStatusLabel(cryptoState?.status, csError)}
                    color={getStatusColor(cryptoState?.status, csError)}
                    size="small"
                  />
                )}
              </ServiceHeader>
              {!csLoading && !csError && cryptoState && (
                <MetricsGrid>
                  <MetricItem>
                    <MetricLabel>MongoDB</MetricLabel>
                    <MetricValue>
                      {cryptoState.database?.mongodb?.status ?? "-"}
                    </MetricValue>
                  </MetricItem>
                  <MetricItem>
                    <MetricLabel>Uptime</MetricLabel>
                    <MetricValue>
                      {formatUptime(cryptoState.uptime ?? 0)}
                    </MetricValue>
                  </MetricItem>
                  <MetricItem>
                    <MetricLabel>Memory</MetricLabel>
                    <MetricValue>
                      {typeof cryptoState.memory?.rss === 'string' 
                        ? cryptoState.memory.rss 
                        : formatBytes(cryptoState.memory?.rss ?? 0)}
                    </MetricValue>
                  </MetricItem>
                </MetricsGrid>
              )}
              {csError && (
                <ErrorText>Service is not responding</ErrorText>
              )}
              <LastChecked>
                {cryptoState?.timestamp
                  ? `Last checked: ${new Date(cryptoState.timestamp).toLocaleTimeString()}`
                  : ""}
              </LastChecked>
            </CardContent>
          </ServiceCard>
        </Grid>
      </Grid>
    </PageWithTitle>
  );
};

const ServiceCard = styled(Card)(({ theme }: any) => ({
  background: theme.palette.background.paper,
  borderRadius: "12px",
  height: "100%",
}));

const ServiceHeader = styled(Box)({
  display: "flex",
  justifyContent: "space-between",
  alignItems: "center",
  marginBottom: "1rem",
});

const ServiceName = styled(Typography)(({ theme }: any) => ({
  fontSize: "1rem",
  fontWeight: 600,
  color: theme.palette.text.primary,
}));

const MetricsGrid = styled(Box)({
  display: "flex",
  flexWrap: "wrap" as const,
  gap: "1rem",
  marginBottom: "0.75rem",
});

const MetricItem = styled(Box)({
  minWidth: "80px",
});

const MetricLabel = styled(Typography)(({ theme }: any) => ({
  fontSize: "0.7rem",
  color: theme.palette.text.secondary,
  textTransform: "uppercase" as const,
  letterSpacing: "0.5px",
}));

const MetricValue = styled(Typography)(({ theme }: any) => ({
  fontSize: "1.1rem",
  fontWeight: 600,
  color: theme.palette.primary.main,
}));

const ErrorText = styled(Typography)(({ theme }: any) => ({
  fontSize: "0.85rem",
  color: theme.palette.error.main,
  marginBottom: "0.75rem",
}));

const LastChecked = styled(Typography)(({ theme }: any) => ({
  fontSize: "0.7rem",
  color: theme.palette.text.secondary,
  marginTop: "0.5rem",
}));
